import axios from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIDispersionDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
  tooltipFormatterScatter,
  tooltipFormatterWithLabel,
} from '../utils/compass'
import { range6m1yAll } from '../utils/config'
import { Palette, datetimeXAxis } from '../utils/constants'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import {
  CommonChartConfig,
  commonChartOptions,
  commonDispersionChartOptions,
} from './common'

export interface CompassAPIPeriodValueLabelDataPoint {
  period: string
  value: number
  label: string
}

interface CompassAPISusepChartData extends CompassAPIDataBase {
  data: CompassAPIPeriodValueLabelDataPoint[]
  title: string
}

interface CompassAPINetDebtEbitdaChartData extends CompassAPIDataBase {
  data: {
    name: string
    data: CompassAPIPeriodValueLabelDataPoint[]
  }[]
  title: string
}

export interface CompassAPIScatterChartData {
  data: CompassAPIDispersionDataPoint[]
}

export namespace Banks {
  export namespace Growth {
    const CONTEXT_ID = 'equity:banks:growth'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'penetration'
        | 'total_loan_yoy'
        | 'total_loan_yoy_type'
        | 'second_derivative_total'
        | 'origin_2m_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Leverage {
    const CONTEXT_ID = 'equity:banks:leverage'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'free_credit_salary'
        | 'commitment_households'
        | 'familias_endividadas'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Default {
    const CONTEXT_ID = 'equity:banks:default'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'npl90'
        | 'npl90_by_type'
        | 'npl90_free'
        | 'npl15_2m'
        | 'npl15_2m_by_type'
        | 'npl_serasa_pj'
        | 'npl_serasa_pj_base100'
        | 'npl_serasa_pf_consumidores_inadimplentes'
        | 'npl_serasa_pf'
        | 'npl_serasa_pf_base100'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'npl_serasa_pf_consumidores_inadimplentes':
            return getConsumidoresInadimplentesChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const isAbsolute = (chart: ChartConfig['chart']) =>
      chart === 'npl_serasa_pj_base100' || chart === 'npl_serasa_pf_base100'

    const getConsumidoresInadimplentesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: data.data.map((series) =>
            series.name.includes('YoY')
          ),
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
          yAxis: [{ id: 'primary' }, { id: 'secondary', opposite: true }].map(
            (yAxis, idx) => ({
              ...yAxis,
              labelSuffix: data.data[idx].name.includes('YoY') ? '%' : '',
            })
          ),
        },
        { yAxis: ['primary', 'secondary'] }
      )(config, data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        ...(!isAbsolute(config.chart)
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        addMinMaxIndicators: true,
        tooltip: {
          xDateFormat: '%B %Y',
          ...(config.chart === 'npl_serasa_pf'
            ? { shared: true, split: false }
            : {}),
        },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)
  }

  export namespace PortfolioDefault {
    const CONTEXT_ID = 'equity:banks:portfolio_default'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'payroll_loan'
        | 'personal_credit'
        | 'vehicle_credit'
        | 'credit_card_credit'
        | 'real_state_credit'
        | 'bndes_rural_yoy'
        | 'households_portfolio'
        | 'households_portfolio_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'households_portfolio':
            return getHouseholdsPortfoliosChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const hasDoubleYAxis = (chart: string) =>
      chart === 'payroll_loan' ||
      chart === 'personal_credit' ||
      chart === 'vehicle_credit' ||
      chart === 'credit_card_credit' ||
      chart === 'real_state_credit' ||
      chart === 'bndes_rural_yoy'

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          convertToPercentages: Array(data.data.length).fill(true),
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
          ...(hasDoubleYAxis(config.chart)
            ? {
                yAxis: [
                  {
                    id: 'relative',
                    title: { text: 'Carteira YoY' },
                    labelSuffix: '%',
                  },
                  { id: 'absolute', title: { text: 'NLP' }, opposite: true },
                ],
              }
            : {}),
        },
        hasDoubleYAxis(config.chart) ? { yAxis: ['relative', 'absolute'] } : {}
      )(config, data)

    const getHouseholdsPortfoliosChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'normal' } },
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)
  }

  export namespace Spread {
    const CONTEXT_ID = 'equity:banks:spread'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'flow' | 'flow_by_type' | 'stock' | 'stock_by_type'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(3).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace ByBank {
    const CONTEXT_ID = 'equity:banks:by_bank'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'loan_growth' | 'share' | 'npl90' | 'coverage_90'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Fees {
    const CONTEXT_ID = 'equity:banks:fees'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'abertura_credito'
        | 'anuidade_cartao'
        | 'doc'
        | 'ted'
        | 'saque'
        | 'pacote_i'
        | 'pacote_iv'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleChartProps(config, data as CompassAPIMultiSeriesChartData),
    }

    const getMultipleChartProps = getMultipleLineChartProps(Context, {
      tooltip: {
        pointFormatter: magnitudePointFormatter(),
        xDateFormat: '%B %Y',
      },
      xAxis: { labels: { format: '{value:%Y}' } },
    })
  }

  export namespace HoldingDiscount {
    const CONTEXT_ID = 'equity:banks:holding_discount'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'historical_discount'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getHistoricalDiscountChartProps(
          config,
          data as CompassAPIMultiSeriesChartData
        ),
    }

    const getHistoricalDiscountChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle: 'ITAUSA - Historical Discount',
        msdIndicators: { avg: true, sd1: true, sd2: true },
        convertToPercentages: [true],
      })(config, data)
  }

  export namespace Alm {
    const CONTEXT_ID = 'equity:banks:alm'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'quarterly' | 'yearly'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'quarterly':
            return getQuarterlyChartProps(config, data)
          case 'yearly':
            return getYearlyChartProps(config, data)
        }
      },
    }

    const getQuarterlyChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: [true, true, true],
          tooltip: { xDateFormat: "Q%q'%y" },
          xAxis: {
            labels: {
              format: "{value:Q%q'%y}",
              rotation: -50,
            },
            tickPositions: data.data[0].data.map((point) =>
              new Date(point.date).getTime()
            ),
          },
          yAxis: [
            { id: 'primary', labelSuffix: '%' },
            { id: 'secondary', opposite: true, labelSuffix: '%' },
          ],
        },
        {
          type: ['spline', 'spline', 'column'],
          yAxis: ['primary', 'primary', 'secondary'],
          zIndex: [100, 100, 10],
        }
      )(config, data)

    const getYearlyChartProps = getMultipleColumnChartProps(
      Context,
      {
        convertToPercentages: Array(4).fill(true),
        tooltip: { xDateFormat: "Q%q'%y" },
        yAxis: [
          { id: 'primary', labelSuffix: '%' },
          { id: 'secondary', opposite: true, labelSuffix: '%' },
        ],
      },
      {
        color: [
          Palette.darkBlue,
          Palette.darkGreen,
          Palette.darkGray,
          Palette.gray,
        ],
        type: ['spline', 'spline', 'column', 'column'],
        yAxis: ['primary', 'primary', 'secondary', 'secondary'],
        zIndex: [100, 100, 10, 10],
      }
    )
  }

  export namespace Rates {
    const CONTEXT_ID = 'equity:banks:rates'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'consignado_publico'
        | 'consignado_publico_banks'
        | 'consignado_inss'
        | 'consignado_inss_banks'
        | 'veiculos'
        | 'veiculos_banks'
        | 'finam_imob_taxas_reguladas'
        | 'finam_imob_taxas_reguladas_banks'
        | 'credito_pessoal_n_consig'
        | 'credito_pessoal_n_consig_banks'
        | 'cheque_especial'
        | 'cheque_especial_banks'
        | 'finam_imob_taxas_mercado'
        | 'finam_imob_taxas_mercado_banks'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Fidcs {
    const CONTEXT_ID = 'equity:banks:fidcs'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'fidcs_fidcs'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config: ChartConfig, data: ChartData) =>
        getMultipleColumnChartProps(
          Context,
          {
            plotOptions: { area: { stacking: 'stacking' } },
            tooltip: {
              pointFormatter: magnitudePointFormatter(),
              xDateFormat: '%B %Y',
            },
          },
          { type: Array(data.data.length).fill('area') }
        )(config, data),
    }
  }

  export namespace Susep {
    const CONTEXT_ID = 'equity:banks:susep'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'susep_total_absolute' | 'susep_total_percent'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPISusepChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config: ChartConfig, data: CompassAPISusepChartData) => {
        const series = [
          {
            data: data.data
              .filter((point) => point.value !== 0)
              .map((point) => ({
                color: point.value > 0 ? Palette.green : Palette.orange,
                y:
                  config.chart === 'susep_total_percent'
                    ? point.value * 100
                    : point.value / 1000000000,
              })),
            dataLabels: {
              enabled: true,
              ...(config.chart === 'susep_total_percent'
                ? { format: '{y:.2f} %' }
                : { format: '{y:.2f} bi' }),
            },
            id: 'single-series',
            marker: { enabled: false },
            name: '',
            showInLegend: false,
            type: 'column',
            yAxis: 'default',
          },
        ]

        const tooltip = {
          split: false,
          valueDecimals: 2,
          ...(config.chart === 'susep_total_percent'
            ? { valueSuffix: '%' }
            : { valueSuffix: 'bi' }),
        }

        const yAxis = [
          {
            id: 'default',
            opposite: false,
            title: { text: '' },
            ...(config.chart === 'susep_total_percent'
              ? { labels: { format: '{value}%' } }
              : { labels: { format: '{value}bi' } }),
          },
        ]

        const xAxis = [
          {
            type: 'category',
            categories: data.data.map((point) => point.label),
          },
        ]

        return {
          id: Context.getChartId(config),
          title: data.title,
          options: {
            ...commonChartOptions,
            ...getDataSources(data.data_sources),
            plotOptions: { column: { minPointLength: 1 } },
            series,
            tooltip,
            xAxis,
            yAxis,
          },
          stockTools: false,
          useRegularChart: true,
        }
      },
    }
  }

  export namespace IFData {
    const CONTEXT_ID = 'equity:banks:if_data'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'quantidade_clientes'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          legend: { maxHeight: 80 },
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            shared: true,
            split: false,
            xDateFormat: '%m/%Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
        })(config, data),
    }
  }

  export namespace PassivoDepositosAbsoluto {
    type PassivoDepositosAbsolutoChartId = 'passivo_depositos_absoluto'

    const CONTEXT_ID = 'equity:banks:if_data:passivo_depositos_absoluto'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: PassivoDepositosAbsolutoChartId
      title?: string
      instituicoes: string[]
      campo: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + '/if-data/passivo-depositos-absoluto',
        method: 'POST',
        data: { instituicoes: config.instituicoes, campo: config.campo },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace PassivoDepositosRelativo {
    type PassivoDepositosRelativoChartId = 'passivo_depositos_relativo'

    const CONTEXT_ID = 'equity:banks:if_data:passivo_depositos_relativo'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: PassivoDepositosRelativoChartId
      title?: string
      instituicao: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + '/if-data/passivo-depositos-relativo',
        method: 'POST',
        data: { instituicao: config.instituicao },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'stacking' } },
          tooltip: { xDateFormat: '%B %Y' },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Carteiras {
    type CarteirasChartId = 'carteiras_quinze_dias' | 'carteiras_total'

    const CARTEIRAS_CHART_PATHS: Record<CarteirasChartId, string> = {
      carteiras_quinze_dias: '/if-data/carteiras-quinze-dias',
      carteiras_total: '/if-data/carteiras-total',
    }

    const CONTEXT_ID = 'equity:banks:if_data:carteiras'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: CarteirasChartId
      title?: string
      instituicoes: string[]
      carteira: string
      isYoy: boolean
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + CARTEIRAS_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          instituicoes: config.instituicoes,
          carteira: config.carteira,
          yoy: config.isYoy,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.isYoy
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.isYoy ? '%' : '',
            }),
            xDateFormat: '%B %Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace CreditoAmpliado {
    const CONTEXT_ID = 'equity:banks:credito_ampliado'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'familias_e_empresas_perc_pib'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:banks:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_itub4'
        | 'pe_bbdc4'
        | 'pe_sanb11'
        | 'pe_brsr6'
        | 'pe_bbsa3'
        | 'pe_bpac11'
        | 'pb_itub4'
        | 'pb_bbdc4'
        | 'pb_sanb11'
        | 'pb_brsr6'
        | 'pb_bbsa3'
        | 'pb_bpac11'
        | 'ratio_pe_itub4_bbdc4'
        | 'ratio_pe_sanb11_itub4'
        | 'ratio_mkt_cap_payments_ex_xp'
        | 'ratio_mkt_cap_xp_itub4'
        | 'ratio_mkt_cap_xp_itub4_ex_xp'
        | 'pe_itub4_ex_xp'
        | 'pb_itub4_ex_xp'
        | 'ratio_mkt_cap_schw_jpm'
        | 'pe_ratio_banks_insurance'
        | 'pe_ratio_banks_domestics'
        | 'pe_ratio_brazilian_banks_em_banks'
        | 'pe_ratio_brazilian_banks_dm_banks'
        | 'irr_itub'
        | 'net_debt_ebitda'
        | 'scatter_roe_pb_by_country'
        | 'scatter_roe_pb_by_company'
      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPINetDebtEbitdaChartData
      | CompassAPIScatterChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'pe_itub4':
          case 'pe_bbdc4':
          case 'pe_sanb11':
          case 'pe_brsr6':
          case 'pe_bbsa3':
          case 'pe_bpac11':
          case 'pb_itub4':
          case 'pb_bbdc4':
          case 'pb_sanb11':
          case 'pb_brsr6':
          case 'pb_bbsa3':
          case 'pb_bpac11':
          case 'ratio_pe_itub4_bbdc4':
          case 'ratio_pe_sanb11_itub4':
          case 'ratio_mkt_cap_payments_ex_xp':
          case 'ratio_mkt_cap_xp_itub4':
          case 'ratio_mkt_cap_xp_itub4_ex_xp':
          case 'pe_itub4_ex_xp':
          case 'pb_itub4_ex_xp':
          case 'ratio_mkt_cap_schw_jpm':
            return getValuationChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'pe_ratio_banks_insurance':
          case 'pe_ratio_banks_domestics':
          case 'pe_ratio_brazilian_banks_em_banks':
          case 'pe_ratio_brazilian_banks_dm_banks':
            return getPeRatioChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'irr_itub':
            return getIrrItubChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
          case 'net_debt_ebitda':
            return getNetDebtEbitdaChartProps(
              config,
              data as CompassAPINetDebtEbitdaChartData
            )
          case 'scatter_roe_pb_by_country':
          case 'scatter_roe_pb_by_company':
            return getScatterChartProps(
              config,
              data as CompassAPIScatterChartData
            )
        }
      },
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getPeRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle:
            config.chart === 'pe_ratio_banks_insurance'
              ? 'PE Ratio Banks/Insurance'
              : config.chart === 'pe_ratio_banks_domestics'
              ? 'PE Ratio Banks/Domestics'
              : config.chart === 'pe_ratio_brazilian_banks_em_banks'
              ? 'PE Ratio Brazilian Banks/EM Banks'
              : 'PE Ratio Brazilian Banks/Developed Banks',
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        { yAxis: ['primary', 'secondary', 'secondary'] }
      )(config, data)

    const getIrrItubChartProps = getMultipleLineChartProps(Context, {
      convertToPercentages: Array(3).fill(true),
      yAxis: [{ id: 'default', labelSuffix: '%' }],
    })

    const getNetDebtEbitdaChartProps = (
      config: ChartConfig,
      data: CompassAPINetDebtEbitdaChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `series-${idx}`,
        type: 'spline',
        marker: { enabled: false },
        name: s.name,
        lineWidth: 2,
        yAxis: 'default',
        data: s.data.map(({ period, value }) => [
          new Date(period).getTime(),
          value,
        ]),
        pointLabels: s.data.map(({ label }) => label),
      }))

      const yAxis = [{ id: 'default', opposite: false, title: { text: ' ' } }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          title: 'Net Debt/EBITDA',
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          tooltip: { pointFormatter: tooltipFormatterWithLabel() },
          series,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getScatterChartProps = (
      config: ChartConfig,
      data: CompassAPIScatterChartData
    ) => {
      const series = data.data.map((point, idx) => ({
        animation: false,
        id: `series-${idx}`,
        name: point.label ?? undefined,
        data: [{ x: point.x, y: point.y }],
      }))

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          xAxisLabel: 'ROE (1BF) - Juros (Median)',
          yAxisLabel: 'PB (1BF) (Median)',
        }),
        shared: false,
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        zIndex: 1000,
      }

      return {
        id: Context.getChartId(config),
        title: `ROE vs. P/B - Juros ${
          config.chart === 'scatter_roe_pb_by_country' ? '(Median)' : ''
        }`,
        options: {
          ...commonChartOptions,
          ...commonDispersionChartOptions,
          ...getDataSources(null),
          series,
          tooltip,
          xAxis: { title: { text: 'ROE (1BF) - Juros (Median)' } },
          yAxis: { title: { text: 'PB (1BF) (Median)' } },
        },
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace Clients {
    type ClientsChartId =
      | 'total'
      | 'total_yoy'
      | 'total_qoq'
      | 'share_by_type_peers'

    const CLIENTS_CHART_PATHS: Record<ClientsChartId, string> = {
      total: '/clients/total',
      total_yoy: '/clients/total-yoy',
      total_qoq: '/clients/total-qoq',
      share_by_type_peers: '/clients/share-by-type-peers',
    }

    const CONTEXT_ID = 'equity:banks:clients'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: ClientsChartId
      companies: string[]
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + CLIENTS_CHART_PATHS[config.chart],
        method: 'POST',
        data: { companies: config.companies },
      }).then((response) => response.data)

    const isShareChart = (chart: ClientsChartId) =>
      chart === 'total_yoy' ||
      chart === 'total_qoq' ||
      chart === 'share_by_type_peers'

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          isShareChart(config.chart) ? 100 * value : value / 1000000,
        ]),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          valueSuffix: isShareChart(config.chart) ? '%' : 'mi',
          xDateFormat: '%B %Y',
        },
        visible:
          (config.chart === 'total_yoy' || config.chart === 'total_qoq') &&
          (s.name === 'C6 BANK (conglomerado)' ||
            s.name === 'NUBANK (conglomerado)')
            ? false
            : true,
        yAxis: 'default',
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          ...(isShareChart(config.chart)
            ? { labels: { format: '{value}%' } }
            : {}),
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            legend: { maxHeight: 80 },
            series,
            yAxis,
            xAxis: datetimeXAxis,
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ConglomeradosFinanceiros {
    type ConglomeradosFinanceirosChartId =
      | 'absolute'
      | 'mom'
      | 'qoq'
      | 'quarterly'

    const CONGLOMERADOS_FINANCEIROS_CHART_PATHS: Record<
      ConglomeradosFinanceirosChartId,
      string
    > = {
      absolute: '/balancetes/conglomerados-financeiros-absolute',
      mom: '/balancetes/conglomerados-financeiros-mom',
      qoq: '/balancetes/conglomerados-financeiros-qoq',
      quarterly: '/balancetes/conglomerados-financeiros-quarterly',
    }

    const CONTEXT_ID = 'equity:banks:conglomerados_financeiros'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: ConglomeradosFinanceirosChartId
      nomesConglomerados: string[]
      conta: number[]
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          BANKS_CHARTS_PATH +
          CONGLOMERADOS_FINANCEIROS_CHART_PATHS[config.chart],
        method: 'POST',
        data: { nome_congl: config.nomesConglomerados, conta: config.conta },
      }).then((response) => response.data)

    const isPercentChart = (chart: ConglomeradosFinanceirosChartId) =>
      chart === 'mom' || chart === 'qoq'

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          isPercentChart(config.chart) ? 100 * value : value / 1000000000,
        ]),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          ...(isPercentChart(config.chart)
            ? { valueSuffix: '%' }
            : { valueSuffix: 'bi' }),
          xDateFormat: '%B %Y',
        },
        yAxis: 'default',
      }))

      const yAxis = {
        id: 'default',
        labels: {
          format: `{value}${isPercentChart(config.chart) ? '%' : 'bi'}`,
        },
        opposite: false,
        title: { text: '' },
      }

      const CHART_TITLES: Record<ConglomeradosFinanceirosChartId, string> = {
        absolute: '(Absolute)',
        mom: '(monthly Y/Y)',
        qoq: '(quarterly Y/Y)',
        quarterly: '(quarterly)',
      }

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: `Conglomerados financeiros ${CHART_TITLES[config.chart]}`,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            legend: { maxHeight: 80 },
            series,
            yAxis,
            xAxis: datetimeXAxis,
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ConglomeradosFinanceirosNet {
    export type ConglomeradosFinanceirosNetChartId =
      | 'absolute_net'
      | 'mom_net'
      | 'qoq_net'
      | 'quarterly_net'

    const CONGLOMERADOS_FINANCEIROS_NET_CHART_PATHS: Record<
      ConglomeradosFinanceirosNetChartId,
      string
    > = {
      absolute_net: '/balancetes/conglomerados-financeiros-net-absolute',
      mom_net: '/balancetes/conglomerados-financeiros-net-mom',
      qoq_net: '/balancetes/conglomerados-financeiros-net-qoq',
      quarterly_net: '/balancetes/conglomerados-financeiros-net-quarterly',
    }

    const CONTEXT_ID = 'equity:banks:conglomerados_financeiros_net'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: ConglomeradosFinanceirosNetChartId
      nomeConglomerado: string
      receita: number
      despesa: number
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          BANKS_CHARTS_PATH +
          CONGLOMERADOS_FINANCEIROS_NET_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          nome_congl: config.nomeConglomerado,
          receita: config.receita,
          despesa: config.despesa,
        },
      }).then((response) => response.data)

    const isPercentChart = (chart: ConglomeradosFinanceirosNetChartId) =>
      chart === 'mom_net' || chart === 'qoq_net'

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          isPercentChart(config.chart) ? 100 * value : value / 1000000000,
        ]),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: '',
        showInLegend: false,
        type: 'spline',
        tooltip: {
          valueDecimals: 2,
          ...(isPercentChart(config.chart)
            ? { valueSuffix: '%' }
            : { valueSuffix: 'bi' }),
          xDateFormat: '%B %Y',
        },
        yAxis: 'default',
      }))

      const yAxis = {
        id: 'default',
        labels: {
          format: `{value}${isPercentChart(config.chart) ? '%' : 'bi'}`,
        },
        opposite: false,
        title: { text: '' },
      }

      const CHART_TITLES: Record<ConglomeradosFinanceirosNetChartId, string> = {
        absolute_net: 'Net (Absolute)',
        mom_net: 'Net (monthly Y/Y)',
        qoq_net: 'Net (quarterly Y/Y)',
        quarterly_net: 'Net (quarterly)',
      }

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: `${config.nomeConglomerado} - Receita (${
          config.receita
        }) Despesa (${config.despesa}) - ${CHART_TITLES[config.chart]}`,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            legend: { maxHeight: 80 },
            series,
            yAxis,
            xAxis: datetimeXAxis,
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ClientsShare {
    const CONTEXT_ID = 'equity:banks:clients_share'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      company: string
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + '/clients/share-by-type',
        method: 'POST',
        data: { companies: [config.company] },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        stacking: 'normal',
        type: 'area',
        tooltip: {
          valueDecimals: 2,
          valueSuffix: '%',
          xDateFormat: '%B %Y',
        },
        yAxis: 'default',
      }))

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
          max: 100,
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            legend: { maxHeight: 80 },
            series,
            yAxis,
            xAxis: datetimeXAxis,
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Neobanks {
    type NeobanksChartId = 'market_cap' | 'sales_rev_turn' | 'net_income'

    const NEOBANKS_CHART_PATHS: Record<NeobanksChartId, string> = {
      market_cap: '/valuation/neobanks/market-cap',
      sales_rev_turn: '/valuation/neobanks/sales-rev-turn',
      net_income: '/valuation/neobanks/net-income',
    }

    const CONTEXT_ID = 'equity:banks:valuation:neobanks'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: NeobanksChartId
      percent: boolean
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
      subtitle: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + NEOBANKS_CHART_PATHS[config.chart],
        method: 'POST',
        data: { percent: config.percent },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(Context, {
        ...(config.percent
          ? { convertToPercentages: Array(data.data.length).fill(true) }
          : {}),
        plotOptions: { column: { stacking: 'normal' } },
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ImpulsoComESemJuros {
    const CONTEXT_ID = 'equity:banks:growth:impulso_com_e_sem_juros'
    const BANKS_CHARTS_PATH = '/api/charts/banks'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      endValuePf: number
      endValuePj: number
      title?: string
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: BANKS_CHARTS_PATH + '/growth/impulso-com-e-sem-juros',
        method: 'POST',
        data: {
          end_value_projection_pf_yoy_growth: config.endValuePf,
          end_value_projection_pj_yoy_growth: config.endValuePj,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%B %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
